import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const usePwaStore = defineStore(
    'pwa',
    () => {
        // State
        const updateExists = ref(false)
        const updateFunction = ref(null)
        const installPromptEvent = ref(null)
        const installed = ref(false)
        const dismissedUpdatePrompt = ref(false)
        const installDismissedAt = ref(null)

        // Getters
        const isInstallable = computed(() => !!installPromptEvent.value)
        // Dismiss logic for install prompt is handled via sessionStorage timestamp
        const PWA_INSTALL_DISMISSED_AT_KEY = 'pwaInstallDismissedAt'
        const PWA_INSTALL_DISMISS_TTL_MS = 15 * 60 * 1000 // 15 minutes

        // Initialize from sessionStorage
        try {
            const raw = sessionStorage.getItem(PWA_INSTALL_DISMISSED_AT_KEY)
            const parsed = raw ? parseInt(raw, 10) : null
            installDismissedAt.value = Number.isNaN(parsed) ? null : parsed
        } catch (e) {
            installDismissedAt.value = null
        }

        const hasRecentInstallDismiss = computed(() => {
            if (!installDismissedAt.value) return false
            return (
                Date.now() - installDismissedAt.value <
                PWA_INSTALL_DISMISS_TTL_MS
            )
        })

        const showInstallPrompt = computed(() => {
            return (
                !!installPromptEvent.value &&
                !installed.value &&
                !hasRecentInstallDismiss.value
            )
        })
        const showUpdatePrompt = computed(
            () => updateExists.value && !dismissedUpdatePrompt.value
        )

        // Actions
        function setUpdate(fn) {
            updateExists.value = true
            updateFunction.value = fn
            dismissedUpdatePrompt.value = false
        }

        async function applyUpdate() {
            updateExists.value = false
            if (updateFunction.value) {
                await updateFunction.value()
            }
        }

        function setInstallPrompt(event) {
            installPromptEvent.value = event
            if (event) {
                installed.value = false
            }
        }

        async function install() {
            if (
                !installPromptEvent.value ||
                typeof installPromptEvent.value.prompt !== 'function'
            ) {
                throw new Error('Install prompt not available')
            }
            try {
                // Show the browser install prompt
                installPromptEvent.value.prompt()

                const { outcome } = await installPromptEvent.value.userChoice
                console.log(`User response to the install prompt: ${outcome}`)

                // The event can only be used once
                installPromptEvent.value = null

                if (outcome === 'accepted') {
                    installed.value = true
                } else {
                    const now = Date.now()
                    try {
                        sessionStorage.setItem(
                            PWA_INSTALL_DISMISSED_AT_KEY,
                            now.toString()
                        )
                    } catch (e) {}
                    installDismissedAt.value = now
                }
            } catch (error) {
                console.error(
                    'PWA install prompt failed; marking dismissed in sessionStorage:',
                    error
                )
                const now = Date.now()
                try {
                    sessionStorage.setItem(
                        PWA_INSTALL_DISMISSED_AT_KEY,
                        now.toString()
                    )
                } catch (e) {}
                installDismissedAt.value = now
                throw error
            }
        }

        function setInstalled(value = true) {
            installed.value = value
        }

        function dismissInstallPrompt() {
            const now = Date.now()
            try {
                sessionStorage.setItem(
                    PWA_INSTALL_DISMISSED_AT_KEY,
                    now.toString()
                )
            } catch (e) {}
            installDismissedAt.value = now
        }

        function dismissUpdatePrompt() {
            dismissedUpdatePrompt.value = true
        }

        return {
            // state
            updateExists,
            updateFunction,
            installPromptEvent,
            installed,
            dismissedUpdatePrompt,
            // getters
            isInstallable,
            showInstallPrompt,
            showUpdatePrompt,
            hasRecentInstallDismiss,
            // actions
            setUpdate,
            applyUpdate,
            setInstallPrompt,
            install,
            setInstalled,
            dismissInstallPrompt,
            dismissUpdatePrompt,
        }
    },
    {
        persist: [
            {
                storage: localStorage,
                paths: ['installed'],
            },
            {
                storage: sessionStorage,
                paths: ['dismissedUpdatePrompt'],
            },
        ],
    }
)
